import { sortingParam } from 'router/declarative/paramRegistry';
import {
  DynamicRouteTitle,
  IndexRouteContent,
  PathRouteContent,
  RouteNode,
} from 'router/declarative/routeNode';
import { AppModules } from '../appContainer/types';
import { paramResourcePermissionBoundary } from '../router/declarative/declarativePermissionBoundary';
import {
  type WorkspaceManagerSection,
  workspaceManagerSections as sections,
} from './sections';

const sectionBoundary = (section: WorkspaceManagerSection) =>
  paramResourcePermissionBoundary(
    { pageName: section.title, message: section.unauthorizedMessage },
    section.requiredPermission,
    ':workspaceId'
  );

export const routes = {
  path: '/workspace-manager',
  title: async (): DynamicRouteTitle =>
    (await import('./streams/title$')).title$,
  appModule: AppModules.WORKSPACE_MANAGER,
  content: paramResourcePermissionBoundary(
    {
      pageName: 'Workspace Manager',
      message:
        'You do not have access to the Workspace Manager. Please contact your administrator if you need access to this feature.',
    },
    'workspace/read',
    ':workspaceId'
  ),

  children: [
    {
      path: '/:workspaceId',
      content: async (): PathRouteContent =>
        (await import('./WorkspaceManagerPage')).WorkspaceManagerPage,
      children: [
        {
          path: { layout: true },
          content: sectionBoundary(sections.settings),
          children: [
            {
              path: sections.settings.path,
              content: async (): IndexRouteContent =>
                (
                  await import('./pages/workspaceSettings/WorkspaceSettingsPage')
                ).DetailsPage,
            },
          ],
        },
        {
          path: sections.metamodel.path,
          content: sectionBoundary(sections.metamodel),
          children: [
            {
              path: { layout: true },
              content: async (): PathRouteContent =>
                (
                  await import('../organizationMetamodel/workspaces/metamodel/MetamodelLayout')
                ).MetamodelLayout,
              children: [
                {
                  path: '/component-types',
                  content: async (): PathRouteContent =>
                    (
                      await import('../organizationMetamodel/workspaces/metamodel/componentTypes/ComponentTypesPanel')
                    ).ComponentTypesPanel,
                  children: [
                    {
                      path: '/:metamodelEntityId',
                      content: async (): PathRouteContent =>
                        (
                          await import('../organizationMetamodel/workspaces/metamodel/componentTypes/sidebar/ComponentTypeSidebar')
                        ).ComponentTypeSidebar,
                    },
                  ],
                },
                {
                  path: '/reference-types',
                  content: async (): PathRouteContent =>
                    (
                      await import('../organizationMetamodel/workspaces/metamodel/referenceTypes/ReferenceTypesPanel')
                    ).ReferenceTypesPanel,
                  children: [
                    {
                      path: '/:metamodelEntityId',
                      content: async (): PathRouteContent =>
                        (
                          await import('../organizationMetamodel/workspaces/metamodel/referenceTypes/sidebar/ReferenceTypeSidebar')
                        ).ReferenceTypeSidebar,
                    },
                  ],
                },
                {
                  path: '/fields',
                  searchParams: { sorting: sortingParam },
                  content: async (): PathRouteContent =>
                    (
                      await import('../organizationMetamodel/workspaces/metamodel/fields/FieldsPanel')
                    ).FieldsPanel,
                  children: [
                    {
                      path: '/:metamodelEntityId',
                      content: async (): PathRouteContent =>
                        (
                          await import('../organizationMetamodel/workspaces/metamodel/fields/sidebar/FieldSidebar')
                        ).FieldSidebar,
                    },
                  ],
                },
                {
                  path: '/graph',
                  content: async (): PathRouteContent =>
                    (
                      await import('./pages/metamodel/MetamodelConstraintsGraph')
                    ).MetamodelConstraintsGraph,
                },
              ],
            },
          ],
        },
        {
          path: { layout: true },
          content: sectionBoundary(sections['workspace-documents']),
          children: [
            {
              path: sections['workspace-documents'].path,
              title: 'Workspace documents - Workspace manager',
              content: async (): PathRouteContent =>
                (
                  await import('./pages/documentArchive/WorkspaceDocumentArchivePage')
                ).WorkspaceDocumentArchivePage,
            },
          ],
        },
        {
          path: { layout: true },
          content: sectionBoundary(sections.history),
          children: [
            {
              path: sections.history.path,
              content: async (): PathRouteContent =>
                (await import('./pages/history/HistoryPage')).HistoryPage,
            },
          ],
        },
        {
          path: { layout: true },
          content: sectionBoundary(sections.assets),
          children: [
            {
              path: sections.assets.path,
              content: async (): PathRouteContent =>
                (await import('./pages/linkedAssets/LinkedAssetsPage'))
                  .LinkedAssetsPage,
            },
          ],
        },
        {
          path: { layout: true },
          content: sectionBoundary(sections.fields),
          children: [
            {
              path: sections.fields.path,
              content: async (): PathRouteContent =>
                (await import('./pages/fields/FieldsPage')).FieldsPage,
            },
          ],
        },
        {
          path: { layout: true },
          content: sectionBoundary(sections.permissions),
          children: [
            {
              path: sections.permissions.path,
              content: async (): PathRouteContent =>
                (await import('./pages/permissions/PermissionsPage'))
                  .PermissionsPage,
            },
          ],
        },
        {
          path: { layout: true },
          content: sectionBoundary(sections['confidential-fields']),
          children: [
            {
              path: sections['confidential-fields'].path,
              content: async (): PathRouteContent =>
                (
                  await import('./pages/confidentialFields/ConfidentialFieldsPage')
                ).ConfidentialFieldsPage,
            },
          ],
        },
      ],
    },
  ],
} as const satisfies RouteNode;
